/**
 * Customization Store - Type Definitions
 *
 * All type definitions, interfaces, and constant data
 * extracted from customizationStore.ts.
 *
 */
import type { ChatBubblePresetId } from '@cgraph-dev/design-tokens';
import {
  DEFAULT_PROFILE_COLOR_ID,
  PROFILE_COLOR_IDS,
  type ChatUiMessageEntranceAnimation,
  type ProfileCardLayoutId,
  type ProfileColorId,
  type ProfileThemeId,
} from '@cgraph-dev/shared-types';
import type {
  ChatThemeBase,
  ChatThemeConversationColor,
  ChatThemeConversationOverride,
  ChatThemeCustomColor,
  ChatThemeCustomColorData,
  ChatThemeCustomColorStore,
  ChatThemeDefaultConversationColor,
  ChatThemeSettings,
  ChatThemeWallpaperPreset,
} from '@cgraph-dev/shared-types/chat-theme';
import {
  DEFAULT_CHAT_THEME_CONVERSATION_COLOR,
  DEFAULT_CHAT_THEME_CUSTOM_COLORS,
} from '@cgraph-dev/shared-types/chat-theme';
import type { AvatarBorderType as SharedAvatarBorderType } from '@cgraph-dev/animation-constants';
import { DEFAULT_CGRAPH_CHAT_WALLPAPER } from '@/modules/chat/theme/cgraph-chat-wallpapers';
import { COLORS as THEME_COLOR_CATALOG } from '@/stores/theme/presets';
import type { ColorDefinition } from '@/stores/theme/types';

// TYPES

export const CUSTOMIZATION_THEME_PRESETS =
  PROFILE_COLOR_IDS satisfies readonly (keyof typeof THEME_COLOR_CATALOG)[];

export type ThemePreset = ProfileColorId;

export type EffectPreset =
  | 'glassmorphism'
  | 'neon'
  | 'holographic'
  | 'minimal'
  | 'aurora'
  | 'cyberpunk';

export type AnimationSpeed = 'slow' | 'normal' | 'fast';

export type AvatarBorderType = SharedAvatarBorderType;

export type ChatBubbleStyle = ChatBubblePresetId;

export type ProfileCardStyle = ProfileCardLayoutId;

export type BubbleAnimation = ChatUiMessageEntranceAnimation;

export interface CustomizationChatThemeSettings extends ChatThemeSettings {
  readonly presetId: string | null;
}

export type ConversationChatThemeOverrides = Readonly<
  Record<string, ChatThemeConversationOverride>
>;

export type ThemeColors = Pick<ColorDefinition, 'primary' | 'secondary' | 'glow' | 'name'>;

export const DEFAULT_CHAT_THEME_SETTINGS: CustomizationChatThemeSettings = {
  base: 'classic',
  presetId: null,
  accentColor: 0x3390ec,
  messageColors: [0x5ca853],
  wallpaper: DEFAULT_CGRAPH_CHAT_WALLPAPER,
};

// STATE INTERFACE

export interface CustomizationState {
  // === Theme Settings ===
  readonly themePreset: ThemePreset;
  readonly effectPreset: EffectPreset;
  readonly animationSpeed: AnimationSpeed;
  readonly particlesEnabled: boolean;
  readonly glowEnabled: boolean;
  readonly blurEnabled: boolean;
  readonly animatedBackground: boolean;

  // === Avatar Settings ===
  readonly avatarBorderType: AvatarBorderType;
  readonly avatarBorderColor: ThemePreset;
  readonly avatarSize: 'small' | 'medium' | 'large';
  readonly selectedBorderTheme: string | null;
  readonly selectedBorderId: string | null;

  // === Chat Settings ===
  readonly chatBubbleStyle: ChatBubbleStyle;
  readonly chatBubbleColor: ThemePreset;
  readonly bubbleBorderRadius: number;
  readonly bubbleShadowIntensity: number;
  readonly bubbleEntranceAnimation: BubbleAnimation;
  readonly bubbleGlassEffect: boolean;
  readonly bubbleShowTail: boolean;
  readonly bubbleHoverEffect: boolean;
  readonly groupMessages: boolean;
  readonly showTimestamps: boolean;
  readonly compactMode: boolean;
  readonly chatThemeSettings: CustomizationChatThemeSettings;
  readonly defaultConversationColor: ChatThemeDefaultConversationColor;
  readonly customChatColors: ChatThemeCustomColorStore;
  readonly conversationChatThemeOverrides: ConversationChatThemeOverrides;

  // === Profile Settings ===
  readonly profileColor: ProfileColorId;
  readonly profileCardStyle: ProfileCardStyle;
  readonly selectedProfileThemeId: ProfileThemeId | null;
  readonly showBadges: boolean;
  readonly showBio: boolean;
  readonly showStatus: boolean;
  readonly glowEffects: boolean;
  readonly particleEffects: boolean;

  // === Title & Badges ===
  readonly equippedTitle: string | null;
  readonly equippedBadges: readonly string[];

  // === Display Name Style ===
  readonly displayNameFont: string;
  readonly displayNameEffect: string;
  readonly displayNameColor: string;
  readonly displayNameSecondaryColor: string | null;

  // === Nameplate ===
  readonly equippedNameplate: string | null;

  // === Profile Theme Preset ===
  readonly profileThemePresetId: string | null;
  readonly profileThemePrimary: string | null;
  readonly profileThemeAccent: string | null;

  // === Canonical Aliases (kept in sync by store actions — consumed throughout codebase) ===
  readonly chatTheme: ThemePreset; // alias for chatBubbleColor
  readonly bubbleStyle: ChatBubbleStyle; // alias for chatBubbleStyle
  readonly messageEffect: BubbleAnimation; // alias for bubbleEntranceAnimation
  readonly avatarBorder: AvatarBorderType; // alias for avatarBorderType
  readonly title: string | null; // alias for equippedTitle
  readonly profileLayout: ProfileCardStyle; // alias for profileCardStyle
  readonly profileTheme: ProfileThemeId | null; // alias for selectedProfileThemeId
  readonly particleEffect: string | null; // particle effect type
  readonly backgroundEffect: string | null; // background effect type
  readonly reactionStyle: string; // reaction animation style
  readonly forumTheme: string | null; // forum theme identifier
  readonly appTheme: ThemePreset; // app-wide theme (alias for themePreset)

  // === Sync State ===
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly lastSyncedAt: number | null;
  readonly error: string | null;
  readonly isDirty: boolean;
}

type MutableCustomizationState = {
  -readonly [K in keyof CustomizationState]: CustomizationState[K];
};

export type CustomizationServerPatch = Partial<MutableCustomizationState>;

// ACTIONS INTERFACE

export interface CustomizationActions {
  // Batch update
  updateSettings: (updates: Partial<CustomizationState>) => void;
  applyServerSettings: (updates: CustomizationServerPatch | Record<string, unknown>) => void;

  // Theme actions
  setTheme: (preset: ThemePreset) => void;
  setEffect: (preset: EffectPreset) => void;
  setAnimationSpeed: (speed: AnimationSpeed) => void;
  toggleParticles: () => void;
  toggleGlow: () => void;
  toggleBlur: () => void;
  toggleAnimatedBackground: () => void;

  // Avatar actions
  setAvatarBorderColor: (color: ThemePreset) => void;
  setAvatarSize: (size: 'small' | 'medium' | 'large') => void;
  selectBorderTheme: (theme: string | null) => void;
  selectBorderId: (id: string | null) => void;

  // Chat actions
  setChatBubbleStyle: (style: ChatBubbleStyle) => void;
  setChatBubbleColor: (color: ThemePreset) => void;
  setBubbleBorderRadius: (radius: number) => void;
  setBubbleShadowIntensity: (intensity: number) => void;
  setBubbleAnimation: (animation: BubbleAnimation) => void;
  toggleBubbleGlass: () => void;
  toggleBubbleTail: () => void;
  toggleBubbleHover: () => void;
  toggleGroupMessages: () => void;
  toggleTimestamps: () => void;
  toggleCompactMode: () => void;
  setChatThemePreset: (base: ChatThemeBase, presetId: string) => void;
  setChatWallpaper: (wallpaper: ChatThemeWallpaperPreset) => void;
  resetChatWallpaper: () => void;
  setDefaultConversationColor: (
    color: ChatThemeConversationColor,
    customColorData?: ChatThemeCustomColorData,
  ) => void;
  setConversationChatThemeColor: (
    conversationId: string,
    color: ChatThemeConversationColor,
    customColorData?: ChatThemeCustomColorData,
  ) => void;
  setConversationChatThemeWallpaper: (
    conversationId: string,
    wallpaper: ChatThemeWallpaperPreset,
  ) => void;
  resetConversationChatThemeWallpaper: (conversationId: string) => void;
  addCustomChatColor: (color: ChatThemeCustomColor, conversationId?: string) => string;
  editCustomChatColor: (colorId: string, color: ChatThemeCustomColor) => void;
  removeCustomChatColor: (colorId: string) => void;
  resetDefaultConversationColor: () => void;
  resetConversationChatThemeColor: (conversationId: string) => void;
  resetAllConversationChatThemeColors: () => void;

  // Profile actions
  setProfileColor: (color: ProfileColorId) => void;
  setProfileCardStyle: (style: ProfileCardStyle) => void;
  setProfileTheme: (themeId: ProfileThemeId | null) => void;
  toggleBadges: () => void;
  toggleBio: () => void;
  toggleStatus: () => void;
  toggleGlowEffects: () => void;
  toggleParticleEffects: () => void;
  setEquippedTitle: (titleId: string | null) => void;
  setEquippedBadges: (badgeIds: string[]) => void;

  // Display name style actions
  setDisplayNameFont: (font: string) => void;
  setDisplayNameEffect: (effect: string) => void;
  setDisplayNameColor: (color: string) => void;
  setDisplayNameSecondaryColor: (color: string | null) => void;

  // Nameplate actions
  setEquippedNameplate: (nameplateId: string | null) => void;

  // Profile theme preset actions
  setProfileThemePreset: (
    presetId: string | null,
    primary: string | null,
    accent: string | null
  ) => void;

  // Sync actions
  fetchCustomizations: (userId?: string) => Promise<void>;
  saveCustomizations: (userId?: string) => Promise<void>;
  resetToDefaults: () => void;
  clearError: () => void;
}

export type CustomizationStore = CustomizationState & CustomizationActions;

// Legacy compatibility export. Theme color metadata is owned by the theme store catalog.
export const THEME_COLORS: Record<ThemePreset, ThemeColors> = {
  emerald: THEME_COLOR_CATALOG.emerald,
  purple: THEME_COLOR_CATALOG.purple,
  cyan: THEME_COLOR_CATALOG.cyan,
  orange: THEME_COLOR_CATALOG.orange,
  pink: THEME_COLOR_CATALOG.pink,
  gold: THEME_COLOR_CATALOG.gold,
  crimson: THEME_COLOR_CATALOG.crimson,
  arctic: THEME_COLOR_CATALOG.arctic,
};

// DEFAULT STATE

export const DEFAULT_STATE: CustomizationState = {
  // Theme
  themePreset: 'purple',
  effectPreset: 'aurora',
  animationSpeed: 'normal',
  particlesEnabled: true,
  glowEnabled: true,
  blurEnabled: true,
  animatedBackground: false,

  // Avatar
  avatarBorderType: 'glow',
  avatarBorderColor: 'purple',
  avatarSize: 'medium',
  selectedBorderTheme: null,
  selectedBorderId: null,

  // Chat
  chatBubbleStyle: 'default',
  chatBubbleColor: 'purple',
  bubbleBorderRadius: 16,
  bubbleShadowIntensity: 30,
  bubbleEntranceAnimation: 'fade',
  bubbleGlassEffect: true,
  bubbleShowTail: true,
  bubbleHoverEffect: true,
  groupMessages: true,
  showTimestamps: true,
  compactMode: false,
  chatThemeSettings: DEFAULT_CHAT_THEME_SETTINGS,
  defaultConversationColor: DEFAULT_CHAT_THEME_CONVERSATION_COLOR,
  customChatColors: DEFAULT_CHAT_THEME_CUSTOM_COLORS,
  conversationChatThemeOverrides: {},

  // Profile
  profileColor: DEFAULT_PROFILE_COLOR_ID,
  profileCardStyle: 'default',
  selectedProfileThemeId: null,
  showBadges: true,
  showBio: true,
  showStatus: true,
  glowEffects: true,
  particleEffects: false,

  // Identity
  equippedTitle: null,
  equippedBadges: [],

  // Display Name Style
  displayNameFont: 'default',
  displayNameEffect: 'solid',
  displayNameColor: '#ffffff',
  displayNameSecondaryColor: null,

  // Nameplate
  equippedNameplate: null,

  // Profile Theme Preset
  profileThemePresetId: null,
  profileThemePrimary: null,
  profileThemeAccent: null,

  // Canonical Aliases (computed at runtime, kept in sync by store actions)
  chatTheme: 'purple',
  bubbleStyle: 'default',
  messageEffect: 'fade',
  avatarBorder: 'glow',
  title: null,
  profileLayout: 'default',
  profileTheme: null,
  particleEffect: null,
  backgroundEffect: null,
  reactionStyle: 'default',
  forumTheme: null,
  appTheme: 'purple',

  // Sync
  isLoading: false,
  isSaving: false,
  lastSyncedAt: null,
  error: null,
  isDirty: false,
};
